import { Link } from "react-router";
import React from "react";
import { graphql } from "~/graphql/codegen";
import { useMutation } from "@apollo/client/react";
import type {
  FollowProfileMutation,
  FollowProfileMutationVariables,
  UnfollowProfileMutation,
  UnfollowProfileMutationVariables,
} from "~/graphql/codegen/graphql";
import useAuthCheck from "~/hooks/check-authenticated";
import { classNames } from "~/utils";
import { Avatar, AvatarImage, Button, toast } from "@mindstonehq/ui";
import { useUser } from "~/hooks/context-hook";
import { useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

const FOLLOW_PROFILE_MUTATION = graphql(`
  mutation followProfile($userID: ID!) {
    followProfile(userID: $userID)
  }
`);

const UNFOLLOW_PROFILE_MUTATION = graphql(`
  mutation unfollowProfile($userID: ID!) {
    unfollowProfile(userID: $userID)
  }
`);

export interface FollowListUser {
  id: string;
  username: string;
  displayName: string;
  profilePictureURL: string;
  tagLine?: string | null;
  following: boolean;
}

export default function FollowListItem({
  profile,
  afterFollow,
  afterUnfollow,
}: {
  profile: FollowListUser;
  afterFollow?: (userID: string) => void;
  afterUnfollow?: (userID: string) => void;
}) {
  const { t } = useLingui();
  const me = useUser();
  const [followProfile] = useMutation<FollowProfileMutation, FollowProfileMutationVariables>(FOLLOW_PROFILE_MUTATION);
  const [unfollowProfile] = useMutation<UnfollowProfileMutation, UnfollowProfileMutationVariables>(
    UNFOLLOW_PROFILE_MUTATION,
  );
  const checkAuth = useAuthCheck();
  const handleFollow = (userID: string, alreadyFollowing: boolean) => {
    const authenticated = checkAuth(t(msg`You need to be logged in to follow users`), `${location.href}`, "follow");

    if (!authenticated) {
      return;
    }
    if (alreadyFollowing) {
      // unfollow
      unfollowProfile({
        variables: {
          userID,
        },
      })
        .then(() => {
          if (afterUnfollow) afterUnfollow(userID);
        })
        .catch((error) => {
          toast.error(t(msg`Error unfollowing user`), {
            description: t(msg`Please try again later.`),
          });
        });
    } else {
      // follow
      followProfile({
        variables: {
          userID,
        },
      })
        .then(() => {
          if (afterFollow) afterFollow(userID);
        })
        .catch((error) => {
          toast.error(t(msg`Error following user`), {
            description: t(msg`Please try again later.`),
          });
        });
    }
  };

  return (
    <Link
      to={`/profile/${profile.username}`}
      className="flex justify-between items-center cursor-pointer py-2 px-5 md:px-4 bg-muted hover:bg-muted/80 space-x-1"
      key={profile.id}
    >
      <div className="flex items-center space-x-2 [&_img]:min-h-[48px] [&_img]:min-w-[48px]">
        <Avatar>
          <AvatarImage src={profile.profilePictureURL}></AvatarImage>
        </Avatar>
        <div className="flex flex-col line-clamp-1">
          <div className="text-base font-medium">{profile.displayName}</div>
          {!!profile.tagLine && <div className="text-sm line-clamp-1">{profile.tagLine}</div>}
          <div className="text-xs text-muted-foreground line-clamp-1">{profile.username}</div>
        </div>
      </div>
      {profile.id !== me?.id && (
        <Button
          variant={profile.following ? "outline" : "default"}
          size={"sm"}
          className={classNames("flex-none w-20")}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleFollow(profile.id, profile.following);
          }}
        >
          {profile.following ? t(msg`Following`) : t(msg`Follow`)}
        </Button>
      )}
    </Link>
  );
}
